import { useNode } from "@craftjs/core";
import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { applyStyleProps, defaultStyleProps, type ResponsiveStyleProps } from "@/lib/craft/style-props";
import { ImageBlockSettings } from "@/components/craft/settings/ImageBlockSettings";

type ImageBlockProps = {
  src: string;
  alt: string;
  mediaId?: string | null;
  objectFit?: "cover" | "contain" | "fill";
  className?: string;
  style?: ResponsiveStyleProps;
};

export function ImageBlock({
  src,
  alt,
  objectFit = "cover",
  className = "aspect-[16/10] w-full rounded-xl",
  style = defaultStyleProps(),
}: ImageBlockProps) {
  const {
    connectors: { connect, drag },
    selected,
    id,
  } = useNode((node) => ({ selected: node.events.selected, id: node.id }));

  const { style: inlineStyle, className: styleClass } = applyStyleProps(style, "desktop", id);
  const merged = mergeStyleForImage(inlineStyle, style);

  return (
    <figure
      ref={(ref) => {
        if (ref) connect(drag(ref));
      }}
      className={cn("my-6", `craft-node-${id}`, styleClass, selected && "outline outline-2 outline-dashed outline-primary/40")}
      style={merged.wrapper}
    >
      <img
        src={src || "/placeholder.svg"}
        alt={alt}
        className={cn(className, "object-cover")}
        style={{ objectFit, ...merged.image }}
      />
    </figure>
  );
}

function mergeStyleForImage(inlineStyle: CSSProperties, responsive: ResponsiveStyleProps) {
  const radius = responsive.desktop.borderRadius;
  return {
    wrapper: inlineStyle,
    image: radius != null ? { borderRadius: `${radius}px` } : {},
  };
}

ImageBlock.craft = {
  displayName: "Image",
  props: {
    src: "",
    alt: "",
    mediaId: null,
    objectFit: "cover",
    className: "aspect-[16/10] w-full rounded-xl",
    style: defaultStyleProps(),
  },
  rules: {
    canDrag: () => true,
  },
  related: {
    settings: ImageBlockSettings,
  },
};
